/* eslint react/jsx-no-bind: 0 */
import AddCoverageCard from '@root/quotes/src/components/coverage-options/add-coverage-card';
import BindParams from '@root/quotes/src/models/bind-params';
import Coverage from '@root/auto-pricing/src/models/coverage';
import CoverageCopy from '@root/quotes/src/components/coverage/coverage-copy';
import CoverageGroup from '@root/quotes/src/components/coverage-options/coverage-group';
import CoverageSelectionCard from '@root/quotes/src/components/coverage-options/coverage-selection-card';
import CoverageTitle from '@root/quotes/src/components/coverage/coverage-title';
import CustomQuote from '@root/quotes/src/models/custom-quote';
import PipAndCoverageStackingExplanationLinkModal from '@root/quotes/src/components/modals/pip-and-coverage-stacking-explanation-link-modal';
import PipLimitsExplanationLinkModal from '@root/quotes/src/components/modals/pip-limits-explanation-link-modal';
import PipResidentRelativesWorkLoss from '@root/quotes/src/components/coverage/pip-resident-relatives-work-loss';
import PipWorkLoss from '@root/quotes/src/components/coverage/pip-work-loss';
import ProfileParams from '@root/auto-pricing/src/models/profile-params';
import PropTypes from '@root/vendor/prop-types';
import QuoteCoveragesContext from '@root/quotes/src/models/quote-coverages-context';
import QuotesContext from '@root/quotes/src/models/quotes-context';
import QuotesSelect from '@root/quotes/src/components/form-elements/quotes-select';
import React, { Component, Fragment } from '@root/vendor/react';

export default class CoverageSectionNonLiability extends Component {
  static propTypes = {
    bindParams: PropTypes.instanceOf(BindParams).isRequired,
    cardHeaderBackgroundColor: PropTypes.string,
    coverageSelections: PropTypes.object,
    customQuote: PropTypes.instanceOf(CustomQuote).isRequired,
    dropdownIndicator: PropTypes.string,
    IconComponent: PropTypes.elementType,
    infoBrandColor: PropTypes.string,
    mobileSelectedOptionStyles: PropTypes.object,
    monthlyPremiumRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onAcceptCoverages: PropTypes.func.isRequired,
    onCoverageChange: PropTypes.func.isRequired,
    onCoverageSelectClose: PropTypes.func.isRequired,
    onCoverageSelectOpen: PropTypes.func.isRequired,
    onDeclineCoverages: PropTypes.func.isRequired,
    onVehicleToggle: PropTypes.func.isRequired,
    optionStyles: PropTypes.object,
    primaryBrandColor: PropTypes.string,
    profileParams: PropTypes.instanceOf(ProfileParams).isRequired,
    quoteCoveragesContext: PropTypes.instanceOf(QuoteCoveragesContext),
    quotesContext: PropTypes.instanceOf(QuotesContext).isRequired,
    renderLearnMore: PropTypes.func,
    secondaryBrandColor: PropTypes.string,
    selectBoxFocusedStyles: PropTypes.object,
    selectBoxStyles: PropTypes.object,
    styleOverrides: PropTypes.object,
  };

  static CoverageTypes = [
    Coverage.Symbols.MEDICAL_EXPENSES,
    Coverage.Symbols.WORK_LOSS,
    Coverage.Symbols.FUNERAL_BENEFITS,
    Coverage.Symbols.UNINSURED_MOTORIST,
    Coverage.Symbols.UNDERINSURED_MOTORIST,
    Coverage.Symbols.UNINSURED_UNDERINSURED_MOTORIST,
    Coverage.Symbols.UNINSURED_MOTORIST_PROPERTY_DAMAGE,
    Coverage.Symbols.UNDERINSURED_MOTORIST_PROPERTY_DAMAGE,
    Coverage.Symbols.PERSONAL_INJURY_PROTECTION,
    Coverage.Symbols.MEDICAL_PAY,
  ]

  static HandleNoVehicleToggleTypes = [
    Coverage.Symbols.PERSONAL_INJURY_PROTECTION,
    Coverage.Symbols.MEDICAL_PAY,
  ]

  _handleAcceptCoverage = (coverage) => {
    if (!CoverageSectionNonLiability.HandleNoVehicleToggleTypes.includes(coverage.symbol)) {
      this.props.onAcceptCoverages([coverage]);
      return;
    }

    const allSelectedVehicles = this.props.profileParams.getAllSelectedVehicles();
    const allSelectedAvailableVins = allSelectedVehicles.map((v) => v.getAvailableVin());
    const coverageWithVins = coverage.set('coveredVins', allSelectedAvailableVins);

    this.props.onAcceptCoverages([coverageWithVins]);
  }

  _renderCoveragePremium(coverage) {
    const {
      bindParams, customQuote, profileParams, monthlyPremiumRatio, quoteCoveragesContext,
    } = this.props;

    const shouldDisplayCoveragePremium =
      coverage.symbol === Coverage.Symbols.UNINSURED_UNDERINSURED_MOTORIST
      && profileParams.market() === 'DE'
      && !coverage.declined;

    if (!shouldDisplayCoveragePremium) {
      return '';
    }

    const profileVins = profileParams.vehicles.map((v) => v.getAvailableVin());
    const coveragePremium = quoteCoveragesContext.getCoveragePremium(
      coverage,
      bindParams.billingCycle,
      customQuote,
      profileVins,
      monthlyPremiumRatio
    );

    return coveragePremium === '$0' ? '' : ` (${coveragePremium})`;
  }

  render() {
    const {
      coverageSelections,
      customQuote,
      dropdownIndicator,
      IconComponent,
      infoBrandColor,
      mobileSelectedOptionStyles,
      onCoverageChange,
      onCoverageSelectClose,
      onCoverageSelectOpen,
      onDeclineCoverages,
      optionStyles,
      quotesContext,
      primaryBrandColor,
      secondaryBrandColor,
      selectBoxFocusedStyles,
      selectBoxStyles,
      styleOverrides,
    } = this.props;
    const coverages = customQuote.getOrderedCoveragesWithOptionsBySymbols(CoverageSectionNonLiability.CoverageTypes, coverageSelections);
    const coverageOptionsStackedAccepted = {
      ...Coverage.DefaultCoverageOptions,
      [Coverage.CoverageOptions.STACKED]: Coverage.CoverageOptionChoice.YES,
    };

    return (
      <CoverageGroup
        heading={'Non-Liability coverage'}
        styleOverrides={styleOverrides}
      >
        {coverages.map((coverage) => {
          const symbol = coverage.symbol;

          if (coverage.symbol === Coverage.Symbols.MEDICAL_PAY && quotesContext.market === 'NJ') {
            return null;
          }

          let onRemoveCoverage = null;

          if (customQuote.coverageIsDeclinable(coverage)) {
            onRemoveCoverage = () => onDeclineCoverages([coverage]);
          }

          if (coverage.symbol === Coverage.Symbols.PERSONAL_INJURY_PROTECTION
            && quotesContext.market === 'NJ') {
            return null;
          }

          const shouldDisplayPipWithWorkLoss = coverage.symbol === Coverage.Symbols.PERSONAL_INJURY_PROTECTION &&
          coverageSelections.pip.some((selection) => Coverage.isCoverageOptionAccepted(selection.coverageOptions, Coverage.CoverageOptions.WORK_LOSS_EXCLUDED));

          const shouldDisplayPipResidentRelativesWorkLoss = coverage.symbol === Coverage.Symbols.PERSONAL_INJURY_PROTECTION &&
            coverageSelections.pip.some((selection) =>
              Coverage.isCoverageOptionAccepted(selection.coverageOptions, Coverage.CoverageOptions.RESIDENT_RELATIVES_INCLUDED)
            && Coverage.isCoverageOptionAccepted(selection.coverageOptions, Coverage.CoverageOptions.WORK_LOSS_EXCLUDED));

          let pipLimitsLink = null;
          if (shouldDisplayPipResidentRelativesWorkLoss) {
            pipLimitsLink = (
              <PipLimitsExplanationLinkModal
                infoBrandColor={infoBrandColor}
                primaryBrandColor={primaryBrandColor}
              />
            );
          } else if (shouldDisplayPipWithWorkLoss) {
            pipLimitsLink = (
              <PipAndCoverageStackingExplanationLinkModal
                infoBrandColor={infoBrandColor}
                primaryBrandColor={primaryBrandColor}
                secondaryBrandColor={secondaryBrandColor}
                showStackingSlide={this.props.profileParams.getAllSelectedVehicles().length > 1}
              />
            );
          }

          let coverageComponent = null;
          if (shouldDisplayPipResidentRelativesWorkLoss) {
            coverageComponent = (
              <PipResidentRelativesWorkLoss
                coverage={coverage}
                coverageSelections={coverageSelections}
                dropdownIndicator={dropdownIndicator}
                infoBrandColor={infoBrandColor}
                mobileSelectedOptionStyles={mobileSelectedOptionStyles}
                onClose={onCoverageSelectClose}
                onCoverageChange={onCoverageChange}
                onOpen={onCoverageSelectOpen}
                optionStyles={optionStyles}
                pipCoverages={customQuote.quoteCoveragesContext.coverages.pip}
                primaryBrandColor={primaryBrandColor}
                secondaryBrandColor={secondaryBrandColor}
              />
            );
          } else if (shouldDisplayPipWithWorkLoss) {
            coverageComponent = (
              <PipWorkLoss
                coverage={coverage}
                coverageSelections={coverageSelections}
                dropdownIndicator={dropdownIndicator}
                infoBrandColor={infoBrandColor}
                mobileSelectedOptionStyles={mobileSelectedOptionStyles}
                onClose={onCoverageSelectClose}
                onCoverageChange={onCoverageChange}
                onOpen={onCoverageSelectOpen}
                optionStyles={optionStyles}
                pipCoverages={customQuote.quoteCoveragesContext.coverages.pip}
                primaryBrandColor={primaryBrandColor}
                profileParams={this.props.profileParams}
                secondaryBrandColor={secondaryBrandColor}
                selectBoxFocusedStyles={selectBoxFocusedStyles}
                selectBoxStyles={selectBoxStyles}
              />
            );
          } else {
            coverageComponent = (
              <QuotesSelect
                dropdownIndicator={dropdownIndicator}
                dropdownIndicatorColor={secondaryBrandColor}
                inputId={`${symbol}-coverage-select`}
                inputName={`${symbol}-coverage-select`}
                mobileSelectedOptionStyles={mobileSelectedOptionStyles}
                onChange={(id) => onCoverageChange(id, symbol)}
                onClose={onCoverageSelectClose}
                onOpen={onCoverageSelectOpen}
                options={coverageSelections[symbol]}
                optionStyles={optionStyles}
                placeholder={'Select option'}
                selectBoxFocusedStyles={selectBoxFocusedStyles}
                selectBoxStyles={selectBoxStyles}
                selectedValue={coverage.id}
              />
            );
          }

          return (
            <Fragment key={symbol}>
              <AddCoverageCard
                description={
                  <CoverageCopy
                    coverage={coverage}
                    market={quotesContext.market}
                  />}
                heading={
                  <CoverageTitle
                    coverage={coverage}
                    market={quotesContext.market}
                  />}
                IconComponent={IconComponent}
                isCoverageAdded={!coverage.declined}
                onAddCoverage={() => this._handleAcceptCoverage(coverage)}
                primaryBrandColor={primaryBrandColor}
              />
              <CoverageSelectionCard
                description={
                  <CoverageCopy
                    additionalLink={pipLimitsLink}
                    coverage={coverage}
                    market={quotesContext.market}
                  />
                }
                headerBackgroundColor={this.props.cardHeaderBackgroundColor}
                heading={
                  <>
                    <CoverageTitle
                      coverage={coverage}
                      market={quotesContext.market}
                    />
                    {this._renderCoveragePremium(coverage)}
                  </>}
                isCoverageAdded={!coverage.declined}
                onRemoveCoverage={onRemoveCoverage}
                primaryBrandColor={primaryBrandColor}
                renderLearnMore={() => !pipLimitsLink && this.props.renderLearnMore(coverageSelections, coverage, coverageOptionsStackedAccepted)}
                vehiclesSelected={true}
              >
                {coverageComponent}
              </CoverageSelectionCard>
            </Fragment>
          );
        })}
      </CoverageGroup>
    );
  }
}

